import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/di/container';
import { QUERY_KEYS } from '@shared/constants';
import type { HistoryListParams } from '@core/repositories/HistoryRepository';

export const useHistory = (workspaceId: string | undefined, params?: HistoryListParams) => {
  return useQuery({
    queryKey: QUERY_KEYS.history(workspaceId ?? '', params),
    queryFn: () => container.history.list(workspaceId as string, params),
    enabled: Boolean(workspaceId),
  });
};

export const useHistoryEntry = (id: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.historyEntry(id ?? ''),
    queryFn: () => container.history.get(id as string),
    enabled: Boolean(id),
  });
};

export const useDeleteHistoryEntry = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => container.history.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history(workspaceId) }),
  });
};

export const useClearHistory = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => container.history.clear(workspaceId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history(workspaceId) }),
  });
};

export const useToggleHistoryFavorite = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFavorited }: { id: string; isFavorited: boolean }) =>
      isFavorited ? container.history.unfavorite(id) : container.history.favorite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history(workspaceId) }),
  });
};
